import React, { useEffect, useMemo, useRef, useState } from "react";

import { useDebouncedCallback } from "use-debounce";

import { EComponentStack } from "@app/state/constants";
import { useFxDispatch, useFxSelector } from "@app/statefx";
import { schema } from "@app/statefx/schema";
import {
  fxCollapsedAllotmentStates,
  fxResizeAllotment,
} from "@app/statefx/xThunks/uiAllotment";

import { useComponentStack } from "./useComponentStack";

type TPaneState = { [key in EComponentStack]: number[] };
type TCollapsedState = { [key in EComponentStack]: boolean };

const snapGrayleft = 537;
const snapGrayRight = 110; //115 will do as 110 is the min size

const initialCollapsedState: TCollapsedState = {
  [EComponentStack.HomePage]: false,
  [EComponentStack.SpvMessages]: false,
  [EComponentStack.InvoiceTracking]: false,
  [EComponentStack.InvoicingInKarma]: false,
  [EComponentStack.InvoiceOverall]: false,
  [EComponentStack.nop]: false,
};

const AllotmentCtx = React.createContext(null);

// idea refactor maybe with a map of refs
export const AllotmentProvider = ({ children }) => {
  const dispatch = useFxDispatch();
  const preserved = useFxSelector(schema.uiAllotment.select);
  const preservedCollapsed = useFxSelector(schema.uiAllotmentCollapsed.select);
  const once = useRef(false);
  const stack = useComponentStack(EComponentStack.nop);

  const componentRefs = {
    [EComponentStack.InvoiceTracking]: useRef(null),
    [EComponentStack.InvoicingInKarma]: useRef(null),
    [EComponentStack.InvoiceOverall]: useRef(null),
    [EComponentStack.SpvMessages]: useRef(null),
  };

  const rightRefs = {
    [EComponentStack.InvoiceTracking]: useRef(null),
    [EComponentStack.InvoicingInKarma]: useRef(null),
    [EComponentStack.InvoiceOverall]: useRef(null),
    [EComponentStack.SpvMessages]: useRef(null),
  };

  const currentDrag = useRef({
    start: [null, null],
    end: [null, null],
  });

  const [collapsedStates, setCollapsedStates] = useState<TCollapsedState>(
    initialCollapsedState
  );

  const [paneStates, setPaneStates] = useState({
    [EComponentStack.InvoiceTracking]: [null, null],
    [EComponentStack.InvoicingInKarma]: [null, null],
    [EComponentStack.InvoiceOverall]: [null, null],
    [EComponentStack.SpvMessages]: [null, null],
  });
  const [currentParent, setCurrentParent] = React.useState(null);
  const [currentRefIsDefined, setRefIsDefined] = useState(false);

  //determine the parent context of the allotment
  const parentCtx = React.useMemo(() => {
    switch (true) {
      case stack.components.includes(EComponentStack.InvoiceOverall):
        setCurrentParent(EComponentStack.InvoiceOverall);
        setRefIsDefined(
          Boolean(componentRefs[EComponentStack.InvoiceOverall].current)
        );
        return [
          componentRefs[EComponentStack.InvoiceOverall],
          rightRefs[EComponentStack.InvoiceOverall],
        ];

      case stack.components.includes(EComponentStack.InvoiceTracking):
        setCurrentParent(EComponentStack.InvoiceTracking);
        setRefIsDefined(
          Boolean(componentRefs[EComponentStack.InvoiceTracking].current)
        );
        return [
          componentRefs[EComponentStack.InvoiceTracking],
          rightRefs[EComponentStack.InvoiceTracking],
        ];

      case stack.components.includes(EComponentStack.InvoicingInKarma):
        setCurrentParent(EComponentStack.InvoicingInKarma);
        setRefIsDefined(
          Boolean(componentRefs[EComponentStack.InvoicingInKarma].current)
        );
        return [
          componentRefs[EComponentStack.InvoicingInKarma],
          rightRefs[EComponentStack.InvoicingInKarma],
        ];
      case stack.components.includes(EComponentStack.SpvMessages):
        setCurrentParent(EComponentStack.SpvMessages);
        setRefIsDefined(
          Boolean(componentRefs[EComponentStack.SpvMessages].current)
        );
        return [
          componentRefs[EComponentStack.SpvMessages],
          rightRefs[EComponentStack.SpvMessages],
        ];

      default:
        return [null, null];
    }
  }, [stack.components, currentRefIsDefined]);

  const debouncedSetState = useDebouncedCallback((value) => {
    setPaneStates((prev) => ({ ...prev, [currentParent]: value }));
  }, 700);

  // todo : create and read custom data-attribs witn min-size of the parentCtx[1]
  // todo : maybe create an animation for the parentCtx[1] to collapse

  // the user clicks to collapse/expand the right panel
  // we keep the newstate in the context but also return the new state to the caller (the Toolbar component)
  const updateStates = (collapsed: boolean) => {
    const newState = { ...collapsedStates };
    switch (currentParent) {
      case EComponentStack.InvoiceTracking:
        newState[EComponentStack.InvoiceTracking] = collapsed;
        break;
      case EComponentStack.InvoicingInKarma:
        newState[EComponentStack.InvoicingInKarma] = collapsed;
        break;
      case EComponentStack.InvoiceOverall:
        newState[EComponentStack.InvoiceOverall] = collapsed;
        break;
      case EComponentStack.SpvMessages:
        newState[EComponentStack.SpvMessages] = collapsed;
        break;
      default:
        break;
    }
    setCollapsedStates(newState);
    dispatch(fxCollapsedAllotmentStates(newState));
  };

  const clickfn = (e) => {
    if (parentCtx?.[1]?.current) {
      if (parentCtx[0].current) {
        if (parentCtx[1].current.offsetWidth <= 115) {
          if (
            !paneStates?.[currentParent]?.[0] ||
            paneStates[currentParent][0] === null
          ) {
            parentCtx[0].current.reset();
          } else {
            parentCtx[0].current.resize(paneStates[currentParent]);
          }
          updateStates(false);
          return false; //in component >> setPanelCollapsed(false);
        } else {
          parentCtx[0].current.resize([Infinity, 100]); //it stops at min 110
          updateStates(true);
          return true; //in component >> setPanelCollapsed(true);
        }
      }
    }
  };

  // sync the state of the right panel with the context
  // it is allocated dinamically and it can start as null.
  const currentCollapsedState = useMemo(() => {
    return collapsedStates[currentParent];
  }, [currentParent, currentRefIsDefined, collapsedStates]);

  // check if the right panel is collapsed
  const isCurrentRightCollapsed = useMemo(() => {
    if (parentCtx?.[1]?.current) {
      return parentCtx?.[1].current.offsetWidth <= 115;
    } else {
      return false;
    }
  }, [currentParent, currentRefIsDefined, currentCollapsedState]);

  const preserveStatePanes = (value: number[]) => {
    if (!currentParent) return;
    if (currentParent in EComponentStack) {
      const payload = { [currentParent]: value } as TPaneState;
      dispatch([
        fxResizeAllotment(payload),
        fxCollapsedAllotmentStates(collapsedStates),
      ]);
      debouncedSetState(value);
    } else {
      console.error(`${currentParent} is not a valid key of EComponentStack.`);
    }
  };

  // manage the dragging and the snapping of the right panel

  const currentDragReset = () => {
    currentDrag.current = {
      start: [null, null],
      end: [null, null],
    };
  };
  const currentDragStart = (e) => {
    currentDragReset();
    currentDrag.current.start = e;
  };
  const isInGrayZone = () => {
    //e[0] is the width of the left pane. could be any value depending on the screen size
    // we control the right pane size
    if (
      currentDrag.current.end[1] > snapGrayRight &&
      currentDrag.current.end[1] <= snapGrayleft
    ) {
      return true;
    } else {
      return false;
    }
  };

  /* 
The "gray zone" is between  537px - snapGrayleft and 110 - snapGrayRight
That means that we don't stop there. (it has no visual use too small for forms to big for buttons);
If the user releases the shash into the gray area, we analyze the direction of the drag.
if the intent is to see more grid then we snap to the right;
if the intent is to see more details then we snap to the left.
-- 
if the user drags ouside the gray area the we just save the expanded dimensions and the collapsed state,
*/
  const markPanePosition = (position: "start" | "end", e: number[]) => {
    if (position === "start") {
      currentDragStart(e);
      return;
    } else {
      currentDrag.current.end = e;
    }
    if (currentDrag.current.end[0] === null) return;
    //direction
    if (currentDrag.current.start[0] > currentDrag.current.end[0]) {
      // fromRightToleft
      if (isInGrayZone()) {
        // we snap to left
        const margin = snapGrayleft - currentDrag.current.end[1];
        const newState = [currentDrag.current.end[0] - margin, snapGrayleft];
        currentDrag.current.end = newState;
        // we don't preserve this state.
        // we don't like clickfn here.
        parentCtx[0].current.resize(newState);
      }
    } else {
      // 'left to right'
      if (isInGrayZone()) {
        // we snap to right
        const fullAllotment =
          currentDrag.current.end[0] + currentDrag.current.end[1];
        const idealRight = fullAllotment - snapGrayRight;
        const margin = idealRight - currentDrag.current.end[0];
        const newState = [currentDrag.current.end[0] + margin, snapGrayRight];
        currentDrag.current.end = newState;
        // we don't preserve this state.
        // we don't like clickfn here.
        parentCtx[0].current.resize(newState);
      }
    }
    //correct the collapsed state (!)
    updateStates(currentDrag.current.end[1] <= 115);
    // preserve the state (!)
    if (currentDrag.current.end[1] > 115) {
      preserveStatePanes(currentDrag.current.end);
    }
  };

  //the new state of the right panel
  useEffect(() => {
    if (currentCollapsedState !== isCurrentRightCollapsed) {
      clickfn(null);
    }
  }, [
    currentParent,
    currentRefIsDefined,
    currentCollapsedState,
    isCurrentRightCollapsed,
  ]);

  useEffect(() => {
    if (!once.current && Object.values(preserved).some((p) => !!p?.[0])) {
      setPaneStates(preserved);
      setCollapsedStates(preservedCollapsed);
      if (parentCtx[0]?.current) {
        if (preservedCollapsed[currentParent]) {
          parentCtx[0].current.resize([Infinity, 100]);
        } else {
          parentCtx[0].current.resize(preserved[currentParent]);
        }
        once.current = true;
      }
    }
  }, [preserved, preservedCollapsed]);

  useEffect(() => {
    if (parentCtx[0]?.current) {
      if (preservedCollapsed[currentParent]) {
        parentCtx[0].current.resize([Infinity, 100]);
      } else {
        parentCtx[0].current.resize(preserved[currentParent]);
      }
    }
  }, [JSON.stringify(stack)]);

  return (
    <AllotmentCtx.Provider
      value={{
        alltRef: parentCtx?.[0],
        rightRef: parentCtx?.[1],
        clickfn,
        currentCollapsedState,
        preserveStatePanes,
        markPanePosition,
      }}
    >
      {children}
    </AllotmentCtx.Provider>
  );
};

export const useAllotmentRef = () => {
  return React.useContext(AllotmentCtx);
};
